import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { formatCurrency, formatCurrencyCompact } from "../../utils/formatters";
import { DEMO_SPENDING_BREAKDOWN } from "./demoData";

const COLORS = [
    "#2563EB", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6",
    "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
    "#a855f7", "#84cc16",
];

function SpendingTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md text-sm">
            <p className="text-gray-900 font-medium">{d.displayName}</p>
            <p className="text-gray-700">{formatCurrency(d.amount)}</p>
            <p className="text-gray-500">{d.percentage}% of spending</p>
            <p className="text-gray-500">{d.transactionCount} transactions</p>
        </div>
    );
}

// Scale spending data based on period (1mo = /6, 3mo = /2, 6mo = full)
function getScaledData(months) {
    const scale = months / 6;
    const data = DEMO_SPENDING_BREAKDOWN;
    return {
        totalSpending: Math.round(data.totalSpending * scale),
        transactionCount: Math.round(data.transactionCount * scale),
        categories: data.categories.map(c => ({
            ...c,
            amount: Math.round(c.amount * scale),
            transactionCount: Math.round(c.transactionCount * scale),
        })),
    };
}

export default function DemoSpendingBreakdownCard({ months = 6, onMonthsChange }) {
    const data = getScaledData(months);

    const topCategories = data.categories.slice(0, 8);
    const otherCategories = data.categories.slice(8);
    const chartCategories = [...topCategories];

    if (otherCategories.length > 0) {
        const otherAmount = otherCategories.reduce((sum, c) => sum + c.amount, 0);
        const otherPct = otherCategories.reduce((sum, c) => sum + c.percentage, 0);
        const otherCount = otherCategories.reduce((sum, c) => sum + c.transactionCount, 0);
        chartCategories.push({
            category: "OTHER",
            displayName: "Other",
            amount: otherAmount,
            percentage: otherPct,
            transactionCount: otherCount,
        });
    }

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Spending Breakdown</h3>
                <div className="flex gap-1 text-xs">
                    {[1, 3, 6].map(m => (
                        <button
                            key={m}
                            onClick={() => onMonthsChange && onMonthsChange(m)}
                            className={`px-2 py-1 rounded ${
                                months === m
                                    ? "bg-accent-100 text-accent-600"
                                    : "text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            {m}mo
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-6">
                <div className="w-48 h-48 flex-shrink-0" style={{ willChange: "transform" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartCategories}
                                cx="50%"
                                cy="50%"
                                innerRadius={45}
                                outerRadius={75}
                                paddingAngle={2}
                                dataKey="amount"
                            >
                                {chartCategories.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<SpendingTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="flex-1 space-y-2 overflow-hidden">
                    {chartCategories.map((cat, i) => (
                        <div key={cat.category} className="flex items-center gap-2 text-sm">
                            <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: COLORS[i % COLORS.length] }}
                            />
                            <span className="text-gray-700 truncate flex-1">{cat.displayName}</span>
                            <span className="text-gray-900 font-medium whitespace-nowrap">
                                {formatCurrencyCompact(cat.amount)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-500">
                    Total: {formatCurrencyCompact(data.totalSpending)} across {data.transactionCount} transactions
                </p>
            </div>
        </div>
    );
}
